import React, { useCallback, useMemo, useState } from 'react';
import {
  Slider as AriaSlider,
  SliderThumb as AriaSliderThumb,
  SliderTrack as AriaSliderTrack,
} from 'react-aria-components';

import { Icon } from '../../assets/Icon/Icon';
import { fade, palette } from '../../common/colors';
import { outlineColors, outlines } from '../../common/outlines';
import { TextInput } from '../../components/TextInput/TextInput';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Small } from '../../text/Small';
import { selectors, transitions } from '../shared/styles';

const sliderValues = {
  thumbSize: 14,
};

const SliderMarker = styled(Small, {
  display: 'flex',
  justifyContent: 'center',
  color: colors.controlContentAccentLight,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  userSelect: 'none',
  transition: transitions.control,

  [darkThemeSelector]: {
    color: colors.controlContentAccentDark,
  },

  [selectors.hover]: {
    color: colors.controlContentValueLight,

    [darkThemeSelector]: {
      color: colors.controlContentValueDark,
    },
  },

  variants: {
    isMin: {
      true: {
        left: 0,
        justifyContent: 'flex-start',
      },
      false: {},
    },
    isMax: {
      true: {
        right: 0,
        justifyContent: 'flex-end',
        marginLeft: `$${sliderValues.thumbSize / 2}`,
      },
      false: {},
    },
    isSelected: {
      true: {
        color: colors.bodyBrandLight,

        [darkThemeSelector]: {
          color: colors.bodyBrandDark,
        },
      },
      false: {},
    },
    positioned: {
      absolute: {
        position: 'absolute',
        top: 0,
        width: 0,
      },
      relative: {
        position: 'relative',
        minWidth: '$16',
      },
    },
  },
});
SliderMarker.displayName = 'SliderMarker';

const SliderMarkers = styled('div', {
  position: 'relative',
  width: '100%',
  heightAll: '$16',
  margin: '$8 0 -$8',
});
SliderMarkers.displayName = 'SliderMarkers';

const SliderThumbIcon = styled(Icon, {
  widthAll: '$8',
  heightAll: '$8',
  color: '$$dotColor',
  transition: transitions.control,

  [darkThemeSelector]: {
    color: '$$dotColor',
  },
});
SliderThumbIcon.displayName = 'SliderThumbIcon';

const SliderThumb = styled(AriaSliderThumb, {
  position: 'absolute',
  zIndex: 3,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: `$${sliderValues.thumbSize}`,
  height: `$${sliderValues.thumbSize}`,
  background: colors.bgApplicationLight,
  outline: outlines.control,
  outlineColor: outlineColors.control.raisedInitialLight,
  boxShadow: shadows.controlRaisedInitialLight,
  borderRadius: '$4',
  transition: transitions.control,
  top: '50%',
  transform: 'translateY(-50%)',
  userSelect: 'none',
  cursor: 'grab',

  $$dotColor: colors.gray200,

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    outlineColor: outlineColors.control.raisedInitialDark,
    boxShadow: shadows.controlRaisedInitialDark,

    $$dotColor: colors.gray600,
  },

  [selectors.hover]: {
    outlineColor: outlineColors.control.raisedHoveredLight,
    boxShadow: shadows.controlRaisedHoveredLight,

    $$dotColor: colors.gray300,

    [darkThemeSelector]: {
      outlineColor: outlineColors.control.raisedHoveredDark,
      boxShadow: shadows.controlRaisedHoveredDark,

      $$dotColor: colors.gray500,
    },
  },

  [selectors.focus]: {
    outlineColor: outlineColors.control.raisedFocusedLight,
    boxShadow: shadows.controlRaisedFocusedLight,

    [darkThemeSelector]: {
      outlineColor: outlineColors.control.raisedFocusedDark,
      boxShadow: shadows.controlRaisedFocusedDark,
    },
  },

  '&[data-disabled]': {
    outlineColor: outlineColors.control.raisedDisabledLight,
    boxShadow: shadows.controlRaisedDisabledLight,

    $$dotColor: colors.gray200,

    [darkThemeSelector]: {
      outlineColor: outlineColors.control.raisedDisabledDark,
      boxShadow: shadows.controlRaisedDisabledDark,

      $$dotColor: colors.gray600,
    },
  },
});
SliderThumb.displayName = 'SliderThumb';

const SliderTrack = styled(AriaSliderTrack, {
  position: 'relative',
  zIndex: 2,
  width: '100%',
  height: '$24',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    height: '$2',
    backgroundColor: colors.tokenBgNeutralLight,
    borderRadius: '$8',
    transform: 'translateY(-50%)',
    zIndex: 0,
    pointerEvents: 'none',

    [darkThemeSelector]: {
      backgroundColor: colors.tokenBgNeutralDark,
    },
  },
});
SliderTrack.displayName = 'SliderTrack';

const SliderRange = styled('div', {
  position: 'absolute',
  top: '50%',
  left: 0,
  right: 0,
  height: '$2',
  backgroundColor: colors.brand600,
  borderRadius: '99em',
  transform: 'translateY(-50%)',
  zIndex: 1,
  pointerEvents: 'none',
  transition: transitions.control,

  '&[data-disabled]': {
    backgroundColor: colors.brand300,

    [darkThemeSelector]: {
      backgroundColor: colors.brand800,
    },
  },
});
SliderRange.displayName = 'SliderRange';

const SliderFields = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$8',
});
SliderFields.displayName = 'SliderFields';

const SliderArea = styled(AriaSlider, {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: '$8',
  width: '100%',
});
SliderArea.displayName = 'SliderArea';

const SliderAreaContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$8',
  padding: '0 $12',
  borderRadius: '$8',
  background: colors.bgApplicationLight,
  strokeAll: fade(palette.controlStrokeBaseLight, 0.12),

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    strokeAll: fade(palette.controlStrokeBaseDark, 0.44),
  },

  variants: {
    disabled: {
      true: {
        background: colors.bgNeutralLight,
        strokeAll: fade(palette.controlStrokeBaseLight, 0.3),
        opacity: 0.5,
        pointerEvents: 'none',

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
          strokeAll: fade(palette.controlStrokeBaseDark, 0.36),
        },
      },
    },
  },
});
SliderAreaContainer.displayName = 'SliderAreaContainer';

const SliderContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: '$8',
  width: '100%',
  userSelect: 'none',
  touchAction: 'none',
});
SliderContainer.displayName = 'SliderContainer';

const SliderTooltip = styled(Small, {
  position: 'absolute',
  zIndex: 10,
  bottom: '100%',
  left: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transform: 'translateX(-50%)',
  minWidth: '$24',
  padding: '$2 $4',
  marginBottom: '$3',
  backgroundColor: colors.bgApplicationLight,
  boxShadow: shadows.overlayLight,
  borderRadius: '$8',
  opacity: 0,
  color: colors.bodyBrandLight,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  userSelect: 'none',
  transition: transitions.control,

  [darkThemeSelector]: {
    backgroundColor: colors.bgApplicationDark,
    boxShadow: shadows.overlayDark,
    color: colors.bodyBrandDark,
  },

  '&[data-visible="true"]': {
    opacity: 1,
  },
});
SliderTooltip.displayName = 'SliderTooltip';

export type SliderProps = {
  /**
   * The current value(s) of the slider.
   */
  value?: number[];
  /**
   * The default value(s) of the slider.
   */
  defaultValue?: number[];
  /**
   * Callback fired when the value changes.
   */
  onValueChange?: (value: number[]) => void;
  /**
   * The minimum value of the slider.
   */
  min?: number;
  /**
   * The maximum value of the slider.
   */
  max?: number;
  /**
   * The step value of the slider.
   */
  step?: number;
  /**
   * Whether the slider is disabled.
   */
  disabled?: boolean;
  /**
   * Whether to show the fields for the slider values.
   */
  showFields?: boolean;
  /**
   * The markers you want to show along the slider.
   */
  markers?: {
    /**
     * The marker you want to show along the slider at the min value.
     */
    min?: React.ReactNode;
    /**
     * The markers you want to show along the slider between the min and max.
     */
    mid?: number[] | React.ReactNode[];
    /**
     * The marker you want to show along the slider at the max value.
     */
    max?: React.ReactNode;
  };
  /**
   * Whether to show the markers on the slider.
   */
  showMarkers?: boolean;
  /**
   * Controls when tooltips are shown for the slider values.
   * - `never`: Disables tooltips entirely
   * - `interaction`: Shows tooltips when hovering or focusing (default behavior)
   * - `always`: Shows tooltips permanently
   */
  showTooltips?: 'never' | 'interaction' | 'always';
  /**
   * The unit of the slider values (e.g. '%', 'Kbps', etc.).
   */
  unit?: React.ReactNode;
};

export const Slider = React.forwardRef<HTMLDivElement, SliderProps>((props, forwardedRef) => {
  const {
    disabled,
    onValueChange,
    markers,
    min = 0,
    max = 100,
    step = 1,
    showFields,
    showMarkers,
    showTooltips = 'always',
    unit,
    value,
    defaultValue,
  } = props;

  const [isHovered, setHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // Memoize event handlers to prevent unnecessary re-renders
  const handleMouseEnter = useCallback(() => setHovered(true), []);
  const handleMouseLeave = useCallback(() => setHovered(false), []);
  const handleFocus = useCallback(() => setIsFocused(true), []);
  const handleBlur = useCallback(() => setIsFocused(false), []);

  const currentValue = useMemo(() => value ?? defaultValue ?? [min], [value, defaultValue, min]);
  const isMin = useMemo(() => currentValue[0] === min, [currentValue, min]);
  const isMax = useMemo(() => currentValue[currentValue.length - 1] === max, [currentValue, max]);

  // Memoize expensive calculations
  const rangeCalculations = useMemo(() => {
    const minValue = Math.min(...currentValue);
    const maxValue = Math.max(...currentValue);
    const range = max - min;

    return {
      minValue,
      maxValue,
      range,
      leftPercentage: currentValue.length === 1 ? 0 : ((minValue - min) / range) * 100,
      rightPercentage:
        currentValue.length === 1
          ? ((max - currentValue[0]) / range) * 100
          : ((max - maxValue) / range) * 100,
    };
  }, [currentValue, min, max]);

  const constrainValue = useCallback(
    (inputValue: number, index: number, values: number[]): number => {
      // First clamp to min/max bounds
      const boundedValue = Math.min(Math.max(inputValue, min), max);

      // For single values, just return the bounded value
      if (values.length === 1) return boundedValue;

      // For multiple values, ensure proper ordering
      const prevValue = index > 0 ? values[index - 1] : -Infinity;
      const nextValue = index < values.length - 1 ? values[index + 1] : Infinity;

      return Math.min(Math.max(boundedValue, prevValue), nextValue);
    },
    [min, max],
  );

  const handleMarkerClick = useCallback(
    (markerValue: number) => {
      if (disabled || !onValueChange) return;

      const distances = currentValue.map((val) => Math.abs(val - markerValue));
      const closestIndex = distances.indexOf(Math.min(...distances));

      const newValues = [...currentValue];
      newValues[closestIndex] = constrainValue(markerValue, closestIndex, currentValue);
      onValueChange(newValues);
    },
    [disabled, onValueChange, currentValue, constrainValue],
  );

  const handleValueChange = useCallback(
    (index: number) => (inputValue: string) => {
      if (!onValueChange) return;

      const num = Number(inputValue);
      if (Number.isNaN(num)) return;

      const newValues = [...currentValue];
      newValues[index] = constrainValue(num, index, currentValue);
      onValueChange(newValues);
    },
    [onValueChange, currentValue, constrainValue],
  );

  return (
    <SliderContainer>
      <SliderAreaContainer
        disabled={disabled}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {showMarkers && (
          <SliderMarkers>
            <SliderMarker
              positioned="absolute"
              isMin
              isSelected={isMin}
              onClick={() => handleMarkerClick(min)}
            >
              {markers?.min ?? `${min}${unit ?? ''}`}
            </SliderMarker>
            {markers?.mid?.map((marker, index) => {
              const markerValue = typeof marker === 'number' ? marker : min + index + 1;
              const position = ((markerValue - min) / rangeCalculations.range) * 100;
              return (
                <SliderMarker
                  key={`markers-mid-${markerValue}`}
                  positioned="absolute"
                  isSelected={currentValue.includes(markerValue)}
                  css={{
                    left: `calc(${position}%)`,
                  }}
                  onClick={() => handleMarkerClick(markerValue)}
                >
                  {marker}
                  {unit}
                </SliderMarker>
              );
            })}
            <SliderMarker
              positioned="absolute"
              isMax
              isSelected={isMax}
              onClick={() => handleMarkerClick(max)}
            >
              {markers?.max ?? `${max}${unit ?? ''}`}
            </SliderMarker>
          </SliderMarkers>
        )}
        <SliderArea
          minValue={min}
          maxValue={max}
          step={step}
          value={currentValue}
          onChange={(val) => onValueChange?.(Array.isArray(val) ? val : [val])}
          isDisabled={disabled}
          ref={forwardedRef}
        >
          <SliderTrack>
            <SliderRange
              css={{
                left: `${rangeCalculations.leftPercentage}%`,
                right: `${rangeCalculations.rightPercentage}%`,
                backgroundColor: colors.brand600,
                height: '$2',
                zIndex: 1,
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            />
            {Array.from({ length: currentValue.length }, (_, index) => {
              const shouldShowTooltip = showTooltips !== 'never';
              const isTooltipVisible =
                showTooltips === 'always' ||
                (showTooltips === 'interaction' && (isHovered || isFocused));

              return (
                <SliderThumb
                  key={`thumb-${index}`}
                  index={index}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                >
                  <SliderThumbIcon icon="slide-horizontal" />
                  {shouldShowTooltip && (
                    <SliderTooltip data-visible={isTooltipVisible}>
                      {currentValue[index]}
                      {unit}
                    </SliderTooltip>
                  )}
                </SliderThumb>
              );
            })}
          </SliderTrack>
        </SliderArea>
      </SliderAreaContainer>
      {showFields && (
        <SliderFields>
          {Array.from({ length: currentValue.length }, (_, i) => (
            <TextInput
              disabled={disabled}
              key={`input-${i}`}
              type="number"
              width="100%"
              value={`${currentValue[i]}`}
              onChange={handleValueChange(i)}
              suffix={unit}
            />
          ))}
        </SliderFields>
      )}
    </SliderContainer>
  );
});
